import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import { extensionVersionPolicyApi, UpdateType, type VersionPolicy } from 'api/extensionVersionPolicy'

// Local-only preview states layered on top of UpdateType — `pending` while the
// preview request is in flight, `error` when the request fails.
type PreviewState = UpdateType | 'pending' | 'error'

const SEMVER_RE = /^\d+\.\d+\.\d+$/
const URL_RE = /^https?:\/\//

const CHROME_STORE_PLACEHOLDER = 'https://chromewebstore.google.com/detail/<extension-id>'

type FormDraft = VersionPolicy & { _exists: boolean }

const emptyDraft = (): FormDraft => ({
    minimumVersion: '',
    recommendedVersion: '',
    storeUrl: '',
    releaseNotes: '',
    _exists: false,
})

function policyToDraft(p?: VersionPolicy | null): FormDraft {
    if (!p) return emptyDraft()
    return {
        minimumVersion: p.minimumVersion,
        recommendedVersion: p.recommendedVersion,
        storeUrl: p.storeUrl,
        releaseNotes: p.releaseNotes ?? '',
        _exists: true,
    }
}

function validateDraft(d: FormDraft): string | null {
    if (!SEMVER_RE.test(d.minimumVersion)) return 'Minimum version must be x.y.z'
    if (!SEMVER_RE.test(d.recommendedVersion)) return 'Recommended version must be x.y.z'
    if (!URL_RE.test(d.storeUrl)) return 'Store URL must start with http:// or https://'
    return null
}

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

export default function ExtensionVersionPolicyPage() {
    const [draft, setDraft] = useState<FormDraft>(emptyDraft())
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)
    const [previewVersion, setPreviewVersion] = useState('3.6.17')
    const [preview, setPreview] = useState<PreviewState | undefined>(undefined)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    useEffect(() => {
        void loadConfig()
    }, [])

    async function loadConfig() {
        setLoading(true)
        try {
            const policy = await extensionVersionPolicyApi.get()
            setDraft(policyToDraft(policy))
        } catch {
            setSnackbar({ open: true, message: 'Failed to load extension version policy', color: 'danger' })
        } finally {
            setLoading(false)
        }
    }

    function updateDraft(patch: Partial<FormDraft>) {
        setDraft((prev) => ({ ...prev, ...patch }))
    }

    async function save() {
        const err = validateDraft(draft)
        if (err) {
            setSnackbar({ open: true, message: err, color: 'danger' })
            return
        }
        setBusy(true)
        try {
            const policy: VersionPolicy = {
                minimumVersion: draft.minimumVersion,
                recommendedVersion: draft.recommendedVersion,
                storeUrl: draft.storeUrl,
                ...(draft.releaseNotes && draft.releaseNotes.length > 0 ? { releaseNotes: draft.releaseNotes } : {}),
            }
            await extensionVersionPolicyApi.setPolicy(policy)
            setDraft((prev) => ({ ...prev, _exists: true }))
            setSnackbar({ open: true, message: 'Extension version policy saved', color: 'success' })
            void runPreview()
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Save failed'
            setSnackbar({ open: true, message: msg, color: 'danger' })
        } finally {
            setBusy(false)
        }
    }

    async function clear() {
        if (!window.confirm('Clear the extension version policy? The extension will fall back to "no update".')) return
        setBusy(true)
        try {
            await extensionVersionPolicyApi.clearPolicy()
            setDraft(emptyDraft())
            setPreview(UpdateType.None)
            setSnackbar({ open: true, message: 'Extension version policy cleared', color: 'success' })
        } catch {
            setSnackbar({ open: true, message: 'Clear failed', color: 'danger' })
        } finally {
            setBusy(false)
        }
    }

    async function runPreview() {
        if (!SEMVER_RE.test(previewVersion)) return
        setPreview('pending')
        try {
            const r = await extensionVersionPolicyApi.preview(previewVersion)
            setPreview(r.updateType)
        } catch {
            setPreview('error')
        }
    }

    const previewVersionValid = SEMVER_RE.test(previewVersion)
    const validation = useMemo(() => validateDraft(draft), [draft])
    const dirtyOrEmpty = !draft.minimumVersion && !draft.recommendedVersion && !draft.storeUrl

    const previewChip = (() => {
        if (!preview) return null
        if (preview === 'pending') return <Chip color="neutral">Checking…</Chip>
        if (preview === 'error') return <Chip color="warning">Preview failed</Chip>
        if (preview === UpdateType.Hard)
            return (
                <Chip color="danger" variant="solid">
                    HARD — would force-update {previewVersion}
                </Chip>
            )
        if (preview === UpdateType.Soft)
            return (
                <Chip color="warning" variant="solid">
                    SOFT — would prompt at {previewVersion}
                </Chip>
            )
        return (
            <Chip color="success" variant="solid">
                NONE — {previewVersion} is current
            </Chip>
        )
    })()

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 900 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
                Extension Version Policy
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3 }}>
                Controls the update prompts for the Numeo Chrome extension. Below the minimum version users get a dismissible update modal (closing it snoozes
                for 24h); below the recommended version they get a native notification + banner. Changes take effect on the extension&apos;s next version check
                (startup / periodic).
            </Typography>

            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
                    <FormControl sx={{ minWidth: 220 }}>
                        <FormLabel>Preview installed version</FormLabel>
                        <Input value={previewVersion} onChange={(e) => setPreviewVersion(e.target.value)} placeholder="3.6.17" />
                        <FormHelperText>{previewVersionValid ? 'Valid semver' : 'Format x.y.z'}</FormHelperText>
                    </FormControl>
                    <Button variant="outlined" onClick={runPreview} disabled={!previewVersionValid}>
                        Run preview
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {previewChip}
                </Stack>
            </Card>

            <Card variant="outlined" sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Typography level="title-lg">Chrome Extension</Typography>
                    {draft._exists ? <Chip color="primary">Active policy</Chip> : <Chip color="neutral">No policy (fail-open)</Chip>}
                </Stack>

                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <FormControl sx={{ minWidth: 220, flex: 1 }} error={Boolean(draft.minimumVersion) && !SEMVER_RE.test(draft.minimumVersion)}>
                            <FormLabel>Minimum version (hard threshold)</FormLabel>
                            <Input
                                value={draft.minimumVersion}
                                onChange={(e) => updateDraft({ minimumVersion: e.target.value })}
                                placeholder="3.6.0"
                                disabled={busy}
                            />
                            <FormHelperText>Below this version, users see a dismissible update modal (closing it snoozes for 24h).</FormHelperText>
                        </FormControl>

                        <FormControl sx={{ minWidth: 220, flex: 1 }} error={Boolean(draft.recommendedVersion) && !SEMVER_RE.test(draft.recommendedVersion)}>
                            <FormLabel>Recommended version (soft threshold)</FormLabel>
                            <Input
                                value={draft.recommendedVersion}
                                onChange={(e) => updateDraft({ recommendedVersion: e.target.value })}
                                placeholder="3.6.17"
                                disabled={busy}
                            />
                            <FormHelperText>Below this (and ≥ minimum), users get a native notification + dismissible banner.</FormHelperText>
                        </FormControl>
                    </Stack>

                    <FormControl error={Boolean(draft.storeUrl) && !URL_RE.test(draft.storeUrl)}>
                        <FormLabel>Store URL</FormLabel>
                        <Input
                            value={draft.storeUrl}
                            onChange={(e) => updateDraft({ storeUrl: e.target.value })}
                            placeholder={CHROME_STORE_PLACEHOLDER}
                            disabled={busy}
                        />
                        <FormHelperText>Where the &quot;Update&quot; button takes the user (the Chrome Web Store listing).</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Release notes (optional)</FormLabel>
                        <Textarea
                            minRows={2}
                            value={draft.releaseNotes ?? ''}
                            onChange={(e) => updateDraft({ releaseNotes: e.target.value })}
                            placeholder="• Faster load board&#10;• Bug fixes"
                            disabled={busy}
                        />
                        <FormHelperText>Shown inside the soft-update prompt.</FormHelperText>
                    </FormControl>

                    {validation && !dirtyOrEmpty ? <Alert color="warning">{validation}</Alert> : null}

                    <Stack direction="row" spacing={2}>
                        <Button onClick={save} disabled={busy || Boolean(validation)} loading={busy}>
                            {draft._exists ? 'Save changes' : 'Publish policy'}
                        </Button>
                        {draft._exists ? (
                            <Button color="danger" variant="soft" onClick={clear} disabled={busy}>
                                Clear policy
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>

                <Typography level="body-xs" sx={{ mt: 2, color: 'text.tertiary' }}>
                    Path: <code>numeo_config.extensionVersion</code>
                </Typography>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                color={snackbar.color}
                variant="soft"
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
